import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import { 
    Document_Role_Enum,
    Document_Status_Enum,
    Document_Type_Enum,
    DocumentFragment,
    Documents_Insert_Input,
    UpdateDocumentMutationVariables
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    TalentAdminDocumentCreateFragment, 
    TalentAdminDocumentCreateSchema, 
    TalentAdminDocumentUpdateFragment, 
    TalentAdminDocumentUpdateSchema 
} from "src/components/talent/documents/TalentAdminDocumentSchemas"
import { extractCurrentItemData } from "src/utils/forms"

type FormPayloadType = TalentAdminDocumentCreateFragment

let initialState: FormPayloadType = {
    type: Document_Type_Enum.Document,
    url: "",
    filename: "",
    size: "",
    filetype: "",
};

export const CreateTalentAdminDocumentDrawer: React.FC<{onClose():void}> = ({ onClose }) => {

    const { createItems, updateItem, deleteItem } = useActions().documents;
    const { loading, errors } = useAppState().documents;
 
    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Documents_Insert_Input) => {
        if (payload.url&&payload.url.length) {
            let newItems = JSON.parse(payload.url)
            let newItemsData = newItems.map((item: {url: string, filename: string, size: string, filetype: string}) => {
                return {
                    role: Document_Role_Enum.Talent,
                    type: payload.type,
                    url: item.url,
                    filename: item.filename,
                    size: item.size,
                    filetype: item.filetype
                }
            })
            return createItems({ objects: newItemsData })
        }
        return Promise.resolve(null)
    }   

    return (
        <>
            <FormDrawer<FormPayloadType, DocumentFragment, string>
                autoOpen={false}
                formSchema={TalentAdminDocumentCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateTalentAdminDocumentAction = () => {
    return (
        <PageDrawerAction<DocumentFragment>
            title='Add Document'
            action={CreateTalentAdminDocumentDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: TalentAdminDocumentUpdateFragment = {
    status: Document_Status_Enum.Active,
};

export const UpdateTalentAdminDocumentDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {

    
    const { updateItem, deleteItem, deselectItem } = useActions().documents;
    const { currentItem, loading, errors } = useAppState().documents;

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: TalentAdminDocumentUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<TalentAdminDocumentUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateDocumentMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<TalentAdminDocumentUpdateFragment, DocumentFragment, string>
                autoOpen={false}
                formSchema={TalentAdminDocumentUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateTalentAdminDocumentAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().documents;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.documents.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<DocumentFragment>
            title='Edit Document'
            action={UpdateTalentAdminDocumentDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditTalentAdminDocumentAction = () => {
    return (
        <PageDrawerAction<DocumentFragment>
            title='Edit Document'
            action={UpdateTalentAdminDocumentDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}